import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useOrders } from "@/hooks/useOrders";
import { useCurrentSession } from "@/hooks/useSession";
import QueryStateWrapper from "@/components/ui/QueryStateWrapper";
import OrderCard from "./OrderCard";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { IoSearch } from "react-icons/io5";
import { getApiInstance } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";

const OrdersPage = () => {
  const navigate = useNavigate();

  // فحص حالة الجلسة
  const { data: sessionData, isLoading: isLoadingSession } =
    useCurrentSession();

  // فحص إذا كانت الجلسة منتهية أو غير موجودة → توجيه إلى صفحة بدء الجلسة بدون تسجيل خروج تلقائي
  useEffect(() => {
    const sessionStarted = localStorage.getItem("sessionStarted");
    // إذا انتهى التحميل ولم توجد جلسة نشطة
    if (!isLoadingSession && !sessionData && sessionStarted) {
      toast.error("لا توجد جلسة نشطة، ابدأ جلسة جديدة");
      navigate("/start-session");
      return;
    }

    // إذا كانت الجلسة موجودة لكنها منتهية
    if (sessionData && sessionData.ended_at && sessionStarted) {
      toast.error("انتهت الجلسة، ابدأ جلسة جديدة");
      navigate("/start-session");
      return;
    }
  }, [sessionData, isLoadingSession, navigate]);

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [selectedTime, setSelectedTime] = useState<string | undefined>(
    undefined
  );
  // تحليلات عدد الطلبات حسب المواعيد (محدّثة باستمرار)
  const api = getApiInstance();
  const { data: analyticsRaw = [], isFetching: isLoadingAnalytics } = useQuery({
    queryKey: ["orders-analytics"],
    queryFn: async () => {
      const { data: resp } = await api.get(`/store/orders/get_analytics/`);
      return Array.isArray(resp) ? resp : [];
    },
    refetchInterval: 10000, // إعادة الجلب كل 10 ثوانٍ
    refetchOnWindowFocus: true,
    staleTime: 5000,
  });
  const analytics: Array<{ time: string; count: number }> = (
    analyticsRaw as any[]
  ).filter((x) => Number(x?.count) > 0);

  // 🔥 اكتشاف الموبايل للـ responsive
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const mobileRegex =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      setIsMobile(mobileRegex.test(userAgent) || window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // جلب تاريخ اليوم بصيغة yyyy-mm-dd
  const today = dayjs().format("YYYY-MM-DD");

  // جلب الطلبات من الـ API مع فلترة اليوم وزمن الفلتر (إن وُجد)
  const { data, isLoading, error } = useOrders(page, today, selectedTime);
  const orders = data?.results || [];
  const showPagination = Boolean(data?.next) || Boolean(data?.previous);

  // (لا حاجة لاستخدام useEffect هنا؛ React Query تتكفّل بالتحديث الدوري)

  // فلترة البحث فقط (بدون فلترة التاريخ)
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const matchesSearch =
        !searchTerm ||
        order.id.toString().includes(searchTerm) ||
        (order.coupon ?? "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (order.user_info?.name || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (order.user_info?.phone || "").includes(searchTerm);
      return matchesSearch;
    });
  }, [orders, searchTerm]);

  const nextPage = () => {
    if (data?.next) setPage((p) => p + 1);
  };

  const prevPage = () => {
    if (page > 1 && data?.previous) setPage((p) => p - 1);
  };

  return (
    <div className={`${isMobile ? "p-3" : "p-4"}`} dir='rtl'>
      <div className='flex items-center justify-between mb-4'>
        <h2 className={`${isMobile ? "text-lg" : "text-xl"} font-bold`}>
          الطلبات
        </h2>
      </div>

      {/* Search أعلى جدول طلبات اليوم */}
      <div className='space-y-4 mb-2' dir='rtl'>
        <div className='flex items-center gap-2'>
          <div className='flex items-center gap-2 bg-white border border-red-300 rounded-full px-4 py-2 shadow-sm w-full'>
            <IoSearch className='text-lg text-red-500' />
            <input
              type='text'
              placeholder='ابحث برقم أو اسم...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className='w-full text-sm bg-transparent outline-none placeholder:text-gray-400 text-right'
            />
          </div>
        </div>
      </div>

      {/* قسم طلبات اليوم (جدول بحد أقصى صفّين) */}
      {analytics.length > 0 && (
        <div className={`${isMobile ? "mt-2" : "mt-3"}`}>
          <h3
            className={`${
              isMobile ? "text-base" : "text-lg"
            } font-bold text-right flex items-center gap-2`}
          >
            <span>طلبات اليوم</span>
            {isLoadingAnalytics && (
              <span className='text-gray-500 text-xs'>جارٍ التحديث...</span>
            )}
          </h3>
          {(() => {
            const total = analytics.length;
            const rows = Math.min(2, total > 0 ? 2 : 0);
            const cols = rows === 0 ? 0 : Math.ceil(total / rows);
            const row1 = analytics.slice(0, cols);
            const row2 = analytics.slice(cols, cols * 2);
            const Cell = ({ time, count }: { time: string; count: number }) => (
              <td
                onClick={() => {
                  setSelectedTime(time);
                  setPage(1);
                }}
                className={`${
                  isMobile ? "px-2 py-2" : "px-3 py-2"
                } border border-red-200 align-top cursor-pointer hover:bg-red-50`}
                title={`عرض طلبات ${time}`}
              >
                <div
                  className={`${
                    isMobile ? "text-xs" : "text-sm"
                  } text-red-700 font-bold`}
                >
                  {count} طلب
                </div>
                <div
                  className={`${
                    isMobile ? "text-[10px]" : "text-xs"
                  } text-gray-700 mt-0.5`}
                >
                  {time}
                </div>
              </td>
            );
            return (
              <table className='w-full border-collapse text-right bg-white rounded-md overflow-hidden'>
                <tbody>
                  <tr>
                    {row1.map((slot, idx) => (
                      <Cell
                        key={`r1-${idx}`}
                        time={slot.time}
                        count={slot.count}
                      />
                    ))}
                  </tr>
                  {row2.length > 0 && (
                    <tr>
                      {row2.map((slot, idx) => (
                        <Cell
                          key={`r2-${idx}`}
                          time={slot.time}
                          count={slot.count}
                        />
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            );
          })()}
          {selectedTime && (
            <div className='mt-2 flex items-center justify-end gap-2'>
              <span className='text-xs text-gray-600'>الوقت المحدد:</span>
              <span className='px-2 py-1 text-xs rounded-full bg-red-50 text-red-700 border border-red-200'>
                {selectedTime}
              </span>
              <button
                type='button'
                onClick={() => {
                  setSelectedTime(undefined);
                  setPage(1);
                }}
                className='text-xs text-red-700 hover:underline'
              >
                إزالة الفلتر
              </button>
            </div>
          )}
        </div>
      )}

      {/* قسم البحث أسفل (تم نقله للأعلى) */}

      <QueryStateWrapper isLoading={isLoading} isError={!!error}>
        <div className={`${isMobile ? "space-y-3" : "space-y-4"} mt-4`}>
          {filteredOrders.length > 0 ? (
            filteredOrders.map((order) => (
              <OrderCard key={order.id} order={order} isMobile={isMobile} />
            ))
          ) : (
            <div
              className={`text-center ${
                isMobile ? "py-6" : "py-8"
              } text-gray-500`}
            >
              <span className={isMobile ? "text-sm" : ""}>
                {orders.length === 0
                  ? "لا توجد طلبات متاحة"
                  : "لا توجد طلبات مطابقة للبحث"}
              </span>
            </div>
          )}
        </div>

        {/* Pagination Controls - محسن للموبايل */}
        {showPagination && data?.results && data.results.length > 0 && (
          <div
            className={`flex justify-center items-center mt-6 gap-4 ${
              isMobile ? "px-4" : ""
            }`}
          >
            <button
              onClick={prevPage}
              disabled={!data.previous}
              className={`$${
                isMobile ? "px-6 py-3 text-sm" : "px-4 py-2"
              } bg-red-100 text-red-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-200 transition`}
            >
              السابق
            </button>

            <div
              className={`flex items-center gap-2 ${
                isMobile ? "text-xs" : "text-sm"
              } text-gray-600`}
            >
              <span>صفحة {page}</span>
              {data?.count && data.results && (
                <span>
                  من {Math.ceil(data.count / (data.results.length || 10))}
                </span>
              )}
            </div>

            <button
              onClick={nextPage}
              disabled={!data.next}
              className={`$${
                isMobile ? "px-6 py-3 text-sm" : "px-4 py-2"
              } bg-red-100 text-red-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-200 transition`}
            >
              التالي
            </button>
          </div>
        )}
      </QueryStateWrapper>
    </div>
  );
};

export default OrdersPage;
